import React from 'react';

import {
    Box,
    Button,
    ColorSwatch,
    Flex,
    Grid,
    Heading,
    Text,
    useChakraContext,
} from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons';

import { Badge } from './../../../components';

const Section = ({ header, children }: { header: string; children?: React.ReactNode }) => (
    <Box mx={5} my={8}>
        <Text mb={4}>{header}</Text>
        {children}
    </Box>
);

const ButtonPreview = ({ colorPalette }: { colorPalette: string }) => {
    return (
        <Box mb={8}>
            <Grid gap={5} templateColumns={'repeat(auto-fill, minmax(160px, auto))'} mb={5}>
                <Button colorPalette={colorPalette}>{colorPalette}</Button>
                <Button colorPalette={colorPalette} disabled>
                    {colorPalette} disabled
                </Button>
                <Button colorPalette={colorPalette} loading>
                    {colorPalette} loading
                </Button>
            </Grid>
            <Grid gap={5} templateColumns={'repeat(auto-fill, minmax(160px, auto))'} mb={5}>
                <Button colorPalette={colorPalette} variant={'solid'}>
                    {colorPalette} solid
                </Button>
                <Button colorPalette={colorPalette} variant={'solid'} disabled>
                    {colorPalette} solid disabled
                </Button>
                <Button colorPalette={colorPalette} variant={'solid'} loading>
                    {colorPalette} solid loading
                </Button>
            </Grid>
            <Grid gap={5} templateColumns={'repeat(auto-fill, minmax(160px, auto))'} mb={5}>
                <Button colorPalette={colorPalette} variant={'plain'}>
                    {colorPalette} plain
                </Button>
                <Button colorPalette={colorPalette} variant={'plain'} disabled>
                    {colorPalette} plain disabled
                </Button>
                <Button colorPalette={colorPalette} variant={'plain'} loading>
                    {colorPalette} plain loading
                </Button>
            </Grid>
        </Box>
    );
};

const BadgePreview = ({ colorPalette }: { colorPalette: string }) => {
    return (
        <Box mb={8}>
            <Grid gap={5} templateColumns={'repeat(auto-fill, minmax(160px, auto))'} mb={5}>
                <Badge colorPalette={colorPalette}>{colorPalette} (subtle)</Badge>
                <Badge colorPalette={colorPalette} variant={'solid'}>
                    {colorPalette} solid
                </Badge>
                <Badge colorPalette={colorPalette} variant={'outline'}>
                    {colorPalette} outline
                </Badge>
            </Grid>
        </Box>
    );
};

const Colors = () => {
    const theme = useChakraContext();
    const colors = theme.tokens.categoryMap.get('colors');
    const allColors = (colors !== undefined ? Array.from(colors.values()) : [])
        .filter(
            color =>
                color.value.startsWith('#') &&
                !['pink', 'teal', 'cyan', 'purple', 'blue', 'red'].includes(color.path[1]),
        )
        .sort((color1, color2) => {
            if (color1.path.length < color2.path.length) {
                return -1;
            } else if (color1.path.length > color2.path.length) {
                return 1;
            }

            return 0;
        });

    let lastColor = '';

    return (
        <Flex gap={3} flexWrap={'wrap'}>
            {allColors.map(color => {
                const withSeparator = lastColor !== color.path[1] && color.path.length > 2;
                lastColor = color.path[1];

                const textStyles = {
                    textAlign: 'center',
                    fontSize: 'xs',
                };

                return (
                    <React.Fragment key={color.name}>
                        {withSeparator ? <Box w={'100%'} /> : null}
                        <Flex flexDirection={'column'}>
                            <ColorSwatch boxSize={'100px'} value={color.value} mb={2} />
                            <Box {...textStyles}>{color.name.replace('colors.', '')}</Box>
                            <Box {...textStyles} opacity={0.75}>
                                {color.value}
                            </Box>
                        </Flex>
                    </React.Fragment>
                );
            })}
        </Flex>
    );
};

export const ThemeSandbox = () => {
    return (
        <>
            <Section header={'Colors'}>
                <Colors />
            </Section>

            <Section header={'Buttons'}>
                <ButtonPreview colorPalette={'brand'} />
                <ButtonPreview colorPalette={'secondary'} />
                <Flex gap={5} mb={5}>
                    <Button colorPalette={'brand'} size={'lg'}>
                        lg button
                    </Button>
                    <Button colorPalette={'brand'} size={'md'}>
                        md button
                    </Button>
                    <Button colorPalette={'brand'} size={'sm'}>
                        sm button
                    </Button>
                </Flex>
                <Flex gap={5} mb={5}>
                    <Button colorPalette={'brand'} size={'lg'}>
                        <IconsLinear.Location />
                        lg button
                    </Button>
                    <Button colorPalette={'brand'} size={'md'}>
                        <IconsLinear.Location />
                        md button
                    </Button>
                    <Button colorPalette={'brand'} size={'sm'}>
                        <IconsLinear.Location />
                        sm button
                    </Button>
                </Flex>
            </Section>

            <Section header={'Badges'}>
                <BadgePreview colorPalette={'brand'} />
                <BadgePreview colorPalette={'secondary'} />
            </Section>

            <Section header={'Headers'}>
                <Flex gap={3} direction={'column'}>
                    <Heading size={'4xl'}>Heading 4xl</Heading>
                    <Heading size={'3xl'}>Heading 3xl</Heading>
                    <Heading size={'2xl'}>Heading 2xl</Heading>
                    <Heading size={'xl'}>Heading xl</Heading>
                    <Heading size={'lg'}>Heading lg</Heading>
                    <Heading size={'md'}>Heading md</Heading>
                    <Heading size={'sm'}>Heading sm</Heading>
                    <Heading size={'xs'}>Heading xs</Heading>
                </Flex>
            </Section>

            <Section header={'Block of text'}>
                <Flex gap={3} direction={'column'}>
                    <Text>
                        Lorem ipsum dolor, sit amet consectetur adipisicing elit. Illo a deleniti,
                        minus, quasi ratione amet, impedit quae cum distinctio totam nihil quo quos
                        voluptatibus? Numquam nihil esse illo dicta expedita!Lorem ipsum dolor, sit
                        amet consectetur adipisicing elit. Illo a deleniti, minus, quasi ratione
                        amet, impedit quae cum distinctio totam nihil quo quos voluptatibus? Numquam
                        nihil esse illo dicta expedita!Lorem ipsum dolor, sit amet consectetur
                        adipisicing elit. Illo a deleniti, minus, quasi ratione amet, impedit quae
                        cum distinctio totam nihil quo quos voluptatibus? Numquam nihil esse illo
                        dicta expedita!Lorem ipsum dolor, sit amet consectetur adipisicing elit.
                        Illo a deleniti, minus, quasi ratione amet, impedit quae cum distinctio
                        totam nihil quo quos voluptatibus? Numquam nihil esse illo dicta
                        expedita!Lorem ipsum dolor, sit amet consectetur adipisicing elit. Illo a
                        deleniti, minus, quasi ratione amet, impedit quae cum distinctio totam nihil
                        quo quos voluptatibus? Numquam nihil esse illo dicta expedita!
                    </Text>
                    <Text>
                        Lorem ipsum dolor, sit amet consectetur adipisicing elit. Illo a deleniti,
                        minus, quasi ratione amet, impedit quae cum distinctio totam nihil quo quos
                        voluptatibus? Numquam nihil esse illo dicta expedita!
                    </Text>
                    <Text>
                        Lorem ipsum dolor, sit amet consectetur adipisicing elit. Illo a deleniti,
                        minus, quasi ratione amet, impedit quae cum distinctio totam nihil quo quos
                        voluptatibus? Numquam nihil esse illo dicta expedita!
                    </Text>
                </Flex>
            </Section>
        </>
    );
};
